/**
 * `06/ Complete Work` (Figma 12:5480) — `/countries/:cc/reports/complete`, v19 D4.
 *
 * Documents by their FIRST deployment (`MIN(deployments.deployed_at)`); a later redeploy
 * does not move a document between windows — the server's disclosure says so and it is
 * rendered. `duration_days` is the SERVER's whole-day count from received to first
 * deployment; nothing here subtracts dates. `reports.view` / export `reports.export`.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { CRF_STAGES, WORK_TYPE_LABEL } from '../crfs/domain';
import { KpiTile } from '../dashboards/dataviz';
import { formatDateLong, formatMoney } from '../inquiries/format';
import { ServerAmount } from '../quotes/money';
import { fetchCompleteReport, reportKeys } from './api';
import type { CompleteReportDto, CompleteRowDto } from './api';
import { ReportFrame } from './ReportFrame';
import type { ReportColumn } from './ReportFrame';
import { useReportUrl } from './report-filters';

function columnsFor(countryCode: string): readonly ReportColumn<CompleteRowDto>[] {
  return [
    {
      key: 'doc',
      header: 'Document',
      width: 'w-[170px]',
      cell: (row) => (
        <Link
          to={`/countries/${countryCode}/crfs/${row.crf_id}`}
          className="font-mono text-mono text-fg underline underline-offset-2 hover:decoration-2"
        >
          {row.doc_no}
        </Link>
      ),
      csv: (row) => row.doc_no,
    },
    { key: 'type', header: 'Work type', width: 'w-[90px]', cell: (row) => WORK_TYPE_LABEL[row.work_type], csv: (row) => WORK_TYPE_LABEL[row.work_type] },
    { key: 'party', header: 'Customer', cell: (row) => row.party.name, csv: (row) => row.party.name },
    {
      key: 'developer',
      header: 'Developer',
      width: 'w-[150px]',
      cell: (row) => row.developer?.name ?? <span className="text-fg-secondary">Unassigned</span>,
      csv: (row) => row.developer?.name ?? 'Unassigned',
    },
    { key: 'received', header: 'Received', width: 'w-[120px]', cell: (row) => formatDateLong(row.received_at), csv: (row) => formatDateLong(row.received_at) },
    { key: 'completed', header: 'First deployed', width: 'w-[120px]', cell: (row) => formatDateLong(row.completed_at), csv: (row) => formatDateLong(row.completed_at) },
    {
      key: 'duration',
      header: 'Duration',
      width: 'w-[100px]',
      numeric: true,
      cell: (row) => `${String(row.duration_days)} ${row.duration_days === 1 ? 'day' : 'days'}`,
      csv: (row) => String(row.duration_days),
    },
    { key: 'deployments', header: 'Deployments', width: 'w-[110px]', numeric: true, cell: (row) => row.deployments, csv: (row) => String(row.deployments) },
    {
      key: 'status',
      header: 'Stage now',
      width: 'w-[140px]',
      cell: (row) => CRF_STAGES[row.status]?.label ?? row.status,
      csv: (row) => CRF_STAGES[row.status]?.label ?? row.status,
    },
    {
      key: 'amount',
      header: 'Amount',
      width: 'w-[130px]',
      numeric: true,
      cell: (row) => <ServerAmount value={row.amount} currency={row.currency} />,
      csv: (row) => formatMoney(row.amount, row.currency),
    },
  ];
}

function Summary({ data }: { data: CompleteReportDto }): React.JSX.Element {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="complete-summary">
      <KpiTile label="Completed" value={data.summary.completed} delta={data.summary.delta} deltaLabel="vs previous window" />
      <KpiTile
        label="Average duration"
        value={data.summary.avg_duration_days === null ? '—' : `${String(data.summary.avg_duration_days)} days`}
        detail="Received to first deployment"
      />
    </div>
  );
}

export function CompleteWorkReportPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const canView = can(countryCode, 'reports.view');
  const canExport = can(countryCode, 'reports.export');
  const { filters } = useReportUrl();

  const query = useQuery({
    queryKey: reportKeys.complete(countryCode, filters),
    queryFn: ({ signal }) => fetchCompleteReport(countryCode, filters, signal),
    enabled: canView,
  });
  const columns = useMemo(() => columnsFor(countryCode), [countryCode]);

  return (
    <ReportFrame
      title="Complete Work"
      testId="report-complete"
      refusal={
        canView
          ? null
          : {
              title: 'Complete Work is not available to this account',
              body: `Viewing reports in ${countryCode} needs the reports permission. Ask an administrator to widen your access.`,
            }
      }
      query={query}
      columns={columns}
      rowKey={(row) => row.crf_id}
      noun={['document', 'documents']}
      summary={(data) => <Summary data={data as CompleteReportDto} />}
      canExport={canExport}
      filename={`${countryCode.toLowerCase()}-report-complete.csv`}
      emptyTitle="Nothing was first deployed in this window."
    />
  );
}
